import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useTranslation } from '../../locales/useTranslation'
import Button from '../shared/Button'

const DAYS_SHORT = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za']
const DAYS_FULL = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag']
const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']

// Convert "HH:MM:SS" or "HH:MM" to minutes from midnight
const toMinutes = (timeStr) => {
  if (!timeStr) return 0
  const [h, m] = timeStr.split(':').map(Number)
  return h * 60 + m
}

// Convert minutes to "HH:MM"
const fromMinutes = (mins) => {
  const h = Math.floor(mins / 60) % 24
  const m = mins % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

const BookingForm = ({ charger, session, onSuccess, onCancel, showToast }) => {
  const { t } = useTranslation()
  const [step, setStep] = useState(1) // 1=date, 2=time, 3=confirm
  const [loading, setLoading] = useState(false)
  const [loadingSlots, setLoadingSlots] = useState(false)

  // 14 days starting today
  const [days, setDays] = useState([])
  const [availableDayIds, setAvailableDayIds] = useState(new Set())

  // Selected values
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedDateObj, setSelectedDateObj] = useState(null)
  const [availableSlots, setAvailableSlots] = useState([])
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [selectedDuration, setSelectedDuration] = useState(1)
  const [message, setMessage] = useState('')
  const [maxDuration, setMaxDuration] = useState(4)

  // Build 14 days
  useEffect(() => {
    const result = []
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    for (let i = 0; i < 14; i++) {
      const d = new Date(today)
      d.setDate(today.getDate() + i)
      result.push(d)
    }
    setDays(result)

    // Figure out which days have availability
    const availDays = new Set()
    charger.charger_availability?.forEach(slot => {
      availDays.add(slot.day_of_week)
    })
    setAvailableDayIds(availDays)
  }, [charger])

  // When date selected, compute available time slots
  const handleDateSelect = async (date) => {
    setSelectedDate(date.toISOString().split('T')[0])
    setSelectedDateObj(date)
    setSelectedSlot(null)
    setLoadingSlots(true)

    try {
      const dayOfWeek = date.getDay() // 0=Sunday

      // Get availability slots for this day of week
      // Note: our DB uses 0=Monday, so convert
      // JS: 0=Sun,1=Mon...6=Sat → DB: 0=Mon...6=Sun
      const dbDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1

      const daySlots = charger.charger_availability?.filter(
        s => s.day_of_week === dbDayOfWeek
      ) || []

      if (daySlots.length === 0) {
        setAvailableSlots([])
        setLoadingSlots(false)
        return
      }

      // Get existing accepted bookings for this charger on this date
      const { data: existingBookings } = await supabase
        .from('bookings')
        .select('start_time, end_time')
        .eq('charger_id', charger.id)
        .eq('date', date.toISOString().split('T')[0])
        .in('status', ['geaccepteerd', 'in_behandeling'])

      // Build available hourly slots
      const slots = []

      daySlots.forEach(avail => {
        const startMins = toMinutes(avail.start_time)
        const endMins = toMinutes(avail.end_time)

        // Generate hourly slots within this availability window
        for (let mins = startMins; mins < endMins - 59; mins += 60) {
          const slotStart = fromMinutes(mins)
          const slotEnd = fromMinutes(mins + 60)

          // Check if this slot overlaps with any existing booking
          const isBooked = existingBookings?.some(booking => {
            const bookStart = toMinutes(booking.start_time)
            const bookEnd = toMinutes(booking.end_time)
            const slotStartM = mins
            const slotEndM = mins + 60
            return slotStartM < bookEnd && slotEndM > bookStart
          })

          if (!isBooked) {
            slots.push({
              start: slotStart,
              end: slotEnd,
              startMins: mins,
              endMins: mins + 60,
              availEndMins: endMins
            })
          }
        }
      })

      setAvailableSlots(slots)
      setStep(2)

    } catch (error) {
      showToast(t('fout'), true)
    } finally {
      setLoadingSlots(false)
    }
  }

  // When time slot selected, compute max duration
  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot)
    setSelectedDuration(1)

    // How many consecutive free slots from this point?
    const slotIndex = availableSlots.findIndex(s => s.start === slot.start)
    let consecutive = 1
    for (let i = slotIndex + 1; i < availableSlots.length; i++) {
      if (availableSlots[i].startMins === availableSlots[i - 1].endMins) {
        consecutive++
      } else {
        break
      }
    }
    // Also cap by availability window end
    const remainingInWindow = Math.floor((slot.availEndMins - slot.startMins) / 60)
    setMaxDuration(Math.min(consecutive, remainingInWindow, 8))
    setStep(3)
  }

  const handleSubmit = async () => {
    if (!selectedDate || !selectedSlot) {
      return showToast('Kies een datum en tijdslot', true)
    }

    setLoading(true)

    try {
      const endTime = fromMinutes(
        toMinutes(selectedSlot.start) + selectedDuration * 60
      )
      const totalPrice = charger.price_per_kwh * selectedDuration * 7.4

      // Final conflict check
      const { data: conflicts } = await supabase
        .from('bookings')
        .select('id')
        .eq('charger_id', charger.id)
        .eq('date', selectedDate)
        .in('status', ['geaccepteerd', 'in_behandeling'])
        .lt('start_time', endTime)
        .gt('end_time', selectedSlot.start)

      if (conflicts && conflicts.length > 0) {
        return showToast('Dit tijdslot is zojuist geboekt. Kies een ander tijdstip.', true)
      }

      const { error } = await supabase
        .from('bookings')
        .insert({
          charger_id: charger.id,
          driver_id: session.user.id,
          owner_id: charger.owner_id,
          date: selectedDate,
          start_time: selectedSlot.start,
          end_time: endTime,
          duration_hours: selectedDuration,
          driver_message: message || null,
          total_price_est: parseFloat(totalPrice.toFixed(2)),
          status: 'in_behandeling'
        })

      if (error) throw error

      await supabase.from('events').insert({
        user_id: session.user.id,
        event_type: 'booking_requested',
        entity_type: 'booking',
        entity_id: charger.id,
        metadata: {
          charger_id: charger.id,
          date: selectedDate,
          duration_hours: selectedDuration
        }
      })

      onSuccess()

    } catch (error) {
      showToast(error.message || t('fout'), true)
    } finally {
      setLoading(false)
    }
  }

  const isDayAvailable = (date) => {
    const dayOfWeek = date.getDay()
    const dbDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1
    return availableDayIds.has(dbDayOfWeek)
  }

  const isToday = (date) => {
    const today = new Date()
    return date.toDateString() === today.toDateString()
  }

  return (
    <div style={{ animation: 'fadeUp 0.3s ease' }}>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        {[1, 2, 3].map(s => (
          <div key={s} style={{
            flex: 1,
            height: 3,
            borderRadius: 2,
            background: s <= step ? 'var(--green)' : 'var(--cream-dark)',
            transition: 'background 0.3s'
          }} />
        ))}
      </div>

      {/* STEP 1: Date picker */}
      {step >= 1 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--ink-soft)',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            marginBottom: 8
          }}>
            {step === 1 ? '📅 Kies een datum' : `📅 ${DAYS_FULL[selectedDateObj?.getDay()]} ${selectedDateObj?.getDate()} ${MONTHS[selectedDateObj?.getMonth()]}`}
          </div>

          {step === 1 && (
            <>
              {/* Day labels */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 3,
                marginBottom: 3
              }}>
                {DAYS_SHORT.map(d => (
                  <div key={d} style={{
                    textAlign: 'center',
                    fontSize: 9,
                    fontWeight: 700,
                    color: 'var(--ink-muted)',
                    textTransform: 'uppercase'
                  }}>
                    {d}
                  </div>
                ))}
              </div>

              {/* 14 day grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 3
              }}>
                {days.map((date, i) => {
                  const available = isDayAvailable(date)
                  const today = isToday(date)
                  const selected = selectedDate === date.toISOString().split('T')[0]

                  return (
                    <button
                      key={i}
                      onClick={() => available && handleDateSelect(date)}
                      disabled={!available}
                      style={{
                        padding: '8px 4px',
                        borderRadius: 10,
                        border: selected
                          ? '2px solid var(--green)'
                          : today
                            ? '1.5px solid var(--green-mid)'
                            : '1px solid transparent',
                        background: selected
                          ? 'var(--green)'
                          : available
                            ? 'var(--green-pale)'
                            : 'var(--cream-dark)',
                        color: selected
                          ? 'white'
                          : available
                            ? 'var(--ink)'
                            : 'var(--ink-muted)',
                        cursor: available ? 'pointer' : 'not-allowed',
                        opacity: available ? 1 : 0.4,
                        fontWeight: today ? 800 : 500,
                        fontSize: 13,
                        fontFamily: 'DM Sans, sans-serif',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1
                      }}>
                      <span style={{ fontSize: 9, opacity: 0.7 }}>
                        {MONTHS[date.getMonth()]}
                      </span>
                      {date.getDate()}
                    </button>
                  )
                })}
              </div>

              {/* Legend */}
              <div style={{
                display: 'flex',
                gap: 12,
                marginTop: 8,
                fontSize: 10,
                color: 'var(--ink-muted)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: 10, height: 10,
                    borderRadius: 3,
                    background: 'var(--green-pale)',
                    border: '1px solid var(--green-mid)'
                  }} />
                  Beschikbaar
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: 10, height: 10,
                    borderRadius: 3,
                    background: 'var(--cream-dark)'
                  }} />
                  Niet beschikbaar
                </div>
              </div>

              {loadingSlots && (
                <div style={{
                  textAlign: 'center',
                  padding: '10px 0',
                  color: 'var(--green)',
                  fontSize: 13
                }}>
                  Tijdslots laden...
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* STEP 2: Time slot picker */}
      {step >= 2 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--ink-soft)',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>
              {step === 2
                ? '⏰ Kies een tijdslot'
                : `⏰ ${selectedSlot?.start} – ${fromMinutes(toMinutes(selectedSlot?.start) + selectedDuration * 60)}`
              }
            </span>
            {step > 2 && (
              <button
                onClick={() => { setStep(2); setSelectedSlot(null) }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--green)',
                  fontSize: 11,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontFamily: 'DM Sans, sans-serif'
                }}>
                Wijzigen
              </button>
            )}
          </div>

          {step === 2 && (
            availableSlots.length === 0 ? (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 12,
                padding: '12px 14px',
                fontSize: 13,
                color: '#991b1b',
                textAlign: 'center'
              }}>
                Geen tijdslots beschikbaar op deze dag. Kies een andere datum.
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 6
              }}>
                {availableSlots.map((slot, i) => (
                  <button
                    key={i}
                    onClick={() => handleSlotSelect(slot)}
                    style={{
                      padding: '10px 4px',
                      borderRadius: 10,
                      border: `1.5px solid var(--green-mid)`,
                      background: 'var(--green-pale)',
                      color: 'var(--green)',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontFamily: 'DM Sans, sans-serif',
                      textAlign: 'center'
                    }}>
                    {slot.start}
                  </button>
                ))}
              </div>
            )
          )}
        </div>
      )}

      {/* STEP 3: Duration + message + confirm */}
      {step >= 3 && selectedSlot && (
        <div>
          {/* Duration */}
          <div style={{ marginBottom: 12 }}>
            <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--ink-soft)',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              marginBottom: 8
            }}>
              ⏱ Duur
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {Array.from({ length: maxDuration }, (_, i) => i + 1).map(h => (
                <button
                  key={h}
                  onClick={() => setSelectedDuration(h)}
                  style={{
                    flex: 1,
                    padding: '10px 0',
                    borderRadius: 10,
                    border: `1.5px solid ${selectedDuration === h ? 'var(--green)' : 'var(--cream-dark)'}`,
                    background: selectedDuration === h ? 'var(--green-pale)' : 'var(--cream)',
                    color: selectedDuration === h ? 'var(--green)' : 'var(--ink-muted)',
                    fontWeight: 800,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontFamily: 'DM Sans, sans-serif'
                  }}>
                  {h}u
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div style={{ marginBottom: 12 }}>
            <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--ink-soft)',
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              marginBottom: 6
            }}>
              💬 {t('bericht')}
            </div>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={t('bericht_placeholder')}
              rows={2}
              style={{
                width: '100%',
                background: 'var(--cream)',
                border: '1.5px solid var(--cream-dark)',
                borderRadius: 12,
                padding: '11px 14px',
                color: 'var(--ink)',
                fontSize: 14,
                resize: 'none',
                fontFamily: 'DM Sans, sans-serif',
                lineHeight: 1.5
              }}
            />
          </div>

          {/* Summary */}
          <div style={{
            background: 'var(--green-pale)',
            border: '1px solid var(--green-mid)',
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 12
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 4
            }}>
              <span style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
                {DAYS_FULL[selectedDateObj?.getDay()]} {selectedDateObj?.getDate()} {MONTHS[selectedDateObj?.getMonth()]}
              </span>
              <span style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
                {selectedSlot.start} – {fromMinutes(toMinutes(selectedSlot.start) + selectedDuration * 60)}
              </span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
                {t('geschatte_kosten')}
              </span>
              <span style={{
                fontFamily: 'Fraunces, serif',
                fontSize: 22,
                fontWeight: 900,
                color: 'var(--green)'
              }}>
                €{(charger.price_per_kwh * selectedDuration * 7.4).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="secondary"
              onClick={onCancel}
              style={{ flex: 1 }}>
              {t('annuleren')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              style={{ flex: 2 }}>
              {loading ? t('laden') : t('boeking_bevestigen')}
            </Button>
          </div>
        </div>
      )}

      {/* Cancel button on step 1 */}
      {step === 1 && (
        <Button
          variant="secondary"
          fullWidth
          onClick={onCancel}
          style={{ marginTop: 8 }}>
          {t('annuleren')}
        </Button>
      )}
    </div>
  )
}

export default BookingForm
